import { normalizeProposalSections } from "@/lib/proposalResultText";
import { appendProposalReferences } from "@/lib/proposalReferences";
import { ProposalBrandingProvider } from "@/components/proposal/ProposalBranding";
import { useState, useEffect } from "react";
import AppLayout from "@/components/layout/AppLayout";
import ProposalForm, { type ProposalFormData } from "@/components/proposal/ProposalForm";
import StaffingTable, {
  type StaffingLineItem,
  createStaffingLineItem,
  calculateStaffingTotals,
  totalOfficerCount,
  staffingFormula,
  requiresClearance,
} from "@/components/proposal/StaffingTable";
import ClientCustomization, { type ClientInfo } from "@/components/proposal/ClientCustomization";
import PhotoUploader, { type PhotoEntry } from "@/components/proposal/PhotoUploader";
import { type AIResults } from "@/components/proposal/ProposalResults";
import ProposalBuilderFlow, { type ProposalStep } from "@/components/proposal/ProposalBuilderFlow";
import SecurityGenerateStep from "@/components/proposal/SecurityGenerateStep";
import ThreatSelector, { type ThreatImportData } from "@/components/proposal/ThreatSelector";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Lock, AlertTriangle, MapPin, DollarSign, Building2, ImagePlus, FileText, Link as LinkIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useWhiteLabel } from "@/contexts/WhiteLabelContext";
import { SUBSCRIPTION_TIERS } from "@/lib/subscription-tiers";
import { Link } from "@/lib/router-compat";
import { useDemoMode } from "@/hooks/useDemoMode";
import { useDraftAutosave } from "@/hooks/useDraftAutosave";
import { DraftRestoreBanner } from "@/components/ui/draft-restore-banner";
import { useEnterpriseCustomFields } from "@/hooks/useEnterpriseCustomFields";
import EnterpriseCustomFieldsStep, { type CustomFieldValues } from "@/components/proposal/EnterpriseCustomFieldsStep";
import EnterpriseCustomFieldsDisplay from "@/components/proposal/EnterpriseCustomFieldsDisplay";
import { Sparkles } from "lucide-react";

const ProposalBuilder = () => {
  const { toast } = useToast();
  const { subscription, profile, refreshProfile, user } = useAuth();
  const { setWhiteLabelSettings } = useWhiteLabel();
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<AIResults | null>(null);
  const [previousResults, setPreviousResults] = useState<AIResults | null>(null);
  const [formData, setFormData] = useState<ProposalFormData | null>(null);

  const [staffingItems, setStaffingItems] = useState<StaffingLineItem[]>([createStaffingLineItem()]);
  const [photos, setPhotos] = useState<PhotoEntry[]>([]);
  const [clientInfo, setClientInfo] = useState<ClientInfo>({
    companyName: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    logoUrl: "",
    accentColor: "#1e3a5f",
    footerText: "",
    removeBranding: false,
  });

  const { fields: customFields, hasFields: hasCustomFields } = useEnterpriseCustomFields("proposal");
  const [customFieldValues, setCustomFieldValues] = useState<CustomFieldValues>({});

  const [selectedThreatId, setSelectedThreatId] = useState<string | null>(null);
  const [hasUserEdits, setHasUserEdits] = useState(false);
  const [importedAiAssessment, setImportedAiAssessment] = useState<string | null>(null);
  const [importedThreatState, setImportedThreatState] = useState<string | null>(null);

  const staffingTotals = calculateStaffingTotals(staffingItems);
  const officerCount = totalOfficerCount(staffingItems);
  const avgPayRate = staffingItems.length > 0 ? staffingItems.reduce((s, i) => s + i.payRate, 0) / staffingItems.length : 25;

  const { isDemoUser, effectiveTier, hasAccess, unlockFeature } = useDemoMode();
  const isSubscribed = isDemoUser ? true : subscription.subscribed;
  const currentTier = isDemoUser ? (effectiveTier as any) : subscription.tier;
  const tierConfig = currentTier ? SUBSCRIPTION_TIERS[currentTier as keyof typeof SUBSCRIPTION_TIERS] : null;
  const isProfessionalOrAbove = isDemoUser
    ? hasAccess("professional")
    : currentTier === "professional" || currentTier === "agency" || currentTier === "enterprise" || currentTier === "testing";
  const isEnterpriseOrStaff = isDemoUser ? false : currentTier === "enterprise" || currentTier === "testing";
  const proposalLimit = tierConfig?.proposalLimit ?? 0;
  const proposalsUsed = profile?.proposals_used_this_month ?? 0;
  const isAtLimit = isDemoUser ? false : (proposalLimit !== null && proposalLimit > 0 && proposalsUsed >= proposalLimit);
  const canGenerate = isSubscribed && !isAtLimit;

  const draftKey = `sf-draft-security-builder:${user?.id ?? "anon"}`;
  const { hasDraft, draftAge, restoreDraft, dismissDraft, clearDraft } = useDraftAutosave({
    storageKey: draftKey,
    state: { formData, clientInfo, staffingItems },
    enabled: !results && !!formData,
    onRestore: (saved: any) => {
      if (saved?.formData) setFormData(saved.formData);
      if (saved?.clientInfo) setClientInfo(saved.clientInfo);
      if (Array.isArray(saved?.staffingItems) && saved.staffingItems.length > 0) {
        setStaffingItems(saved.staffingItems);
      }
    },
  });

  useEffect(() => {
    if (isEnterpriseOrStaff) {
      setWhiteLabelSettings({
        companyName: clientInfo.companyName,
        removeBranding: clientInfo.removeBranding,
        accentColor: clientInfo.accentColor,
      });
    }
  }, [clientInfo.companyName, clientInfo.removeBranding, clientInfo.accentColor, isEnterpriseOrStaff, setWhiteLabelSettings]);

  const getComputedCost = () => {
    const total = staffingTotals.weekly;
    const formula = staffingFormula(staffingItems);
    return { total, formula, period: "Weekly", modelLabel: "Multi-Role Staffing" };
  };

  const handleThreatSelect = (data: ThreatImportData | null) => {
    if (!data) {
      setSelectedThreatId(null);
      setImportedAiAssessment(null);
      setImportedThreatState(null);
      return;
    }
    setSelectedThreatId(data.threatId);
    setImportedAiAssessment(data.aiGeneratedAssessment || null);
    setImportedThreatState(data.state || null);
    // Auto-fill notes with threat data
    if (formData) {
      setFormData({
        ...formData,
        notes: data.notes ? (formData.notes ? `${formData.notes}\n\n${data.notes}` : data.notes) : formData.notes,
      });
    }
    setHasUserEdits(false);
  };

  const handleGenerate = async (additionalContext?: string) => {
    if (!formData || !canGenerate) {
      if (!isSubscribed) {
        toast({ title: "Subscription Required", description: "Please subscribe to generate proposals.", variant: "destructive" });
      } else if (isAtLimit) {
        toast({ title: "Limit Reached", description: `You've used all ${proposalLimit} proposals. Upgrade your plan for more.`, variant: "destructive" });
      }
      return;
    }
    if (staffingItems.length === 0 || staffingTotals.weekly <= 0) {
      toast({ title: "Missing Input", description: "Please add at least one staffing position.", variant: "destructive" });
      return;
    }

    setIsLoading(true);
    const costInfo = getComputedCost();
    const isRefinement = !!additionalContext && !!results;

    try {
      const { data: rawResult, error } = await supabase.functions.invoke("generate-proposal", {
        body: {
          address: `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip}`,
          propertyType: formData.propertyType,
          serviceType: formData.serviceType,
          officers: officerCount,
          armedStatus: staffingItems.some((i) => i.armedStatus === "armed") ? "armed" : "unarmed",
          pricingModel: "multi_role",
          pricingModelLabel: costInfo.modelLabel,
          payRate: avgPayRate,
          totalCost: costInfo.total,
          costFormula: costInfo.formula,
          costPeriod: costInfo.period,
          staffingLineItems: staffingItems.map((item) => ({
            roleType: item.roleType,
            quantity: item.quantity,
            armedStatus: item.armedStatus,
            clearanceLevel: item.clearanceLevel,
            payRate: item.payRate,
            hoursPerDay: item.hoursPerDay,
            daysPerWeek: item.daysPerWeek,
          })),
          notes: appendProposalReferences(formData.notes, clientInfo.references),
          state: importedThreatState || formData.state,
          clientInfo: clientInfo.companyName ? clientInfo : undefined,
          photoDescriptions: photos
            .filter((p) => p.description)
            .map((p, i) => ({ label: `Image ${i + 1}`, description: p.description })),
          importedThreatAssessment: importedAiAssessment || undefined,
          additionalContext: additionalContext || undefined,
          previousProposal: isRefinement ? results : undefined,
        },
      });
      if (error) throw error;
      const result = normalizeProposalSections(rawResult);
      const aiResults = result as AIResults;
      // Capture previous results BEFORE updating, so the diff summary can compare
      setPreviousResults(isRefinement ? results : null);
      setResults(aiResults);

      if (user && !isRefinement) {
        await supabase.from("proposals").insert({
          user_id: user.id,
          address: `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip}`,
          property_type: formData.propertyType,
          officers: officerCount,
          armed_status: staffingItems.some((i) => i.armedStatus === "armed") ? "armed" : "unarmed",
          days_of_coverage: staffingItems[0]?.daysPerWeek ?? 5,
          hours_of_coverage: staffingItems[0]?.hoursPerDay ?? 8,
          hourly_rate: avgPayRate,
          notes: formData.notes || null,
          risk_level: aiResults.riskLevel,
          executive_summary: aiResults.executiveSummary,
          threat_assessment: aiResults.threatAssessment,
          risk_impact: aiResults.riskImpact,
          staffing_recommendation: aiResults.staffingRecommendation,
          security_actions: aiResults.securityActions,
          implementation_plan: aiResults.implementationPlan,
          expected_outcomes: aiResults.expectedOutcomes,
          crime_data_summary: aiResults.crimeDataSummary || null,
          source: "web",
          industry: "security",
          client_name: clientInfo.contactName || null,
          client_company: clientInfo.companyName || null,
          client_email: clientInfo.contactEmail || null,
          total_cost: costInfo.total,
          proposal_data: {
            branding: clientInfo,
            references: clientInfo.references ?? [],
            generatedResults: aiResults,
            serviceType: formData.serviceType,
            pricingModel: "multi_role",
            pricingModelLabel: costInfo.modelLabel,
            costFormula: costInfo.formula,
            costPeriod: costInfo.period,
            staffingLineItems: staffingItems,
            customFields: hasCustomFields ? customFieldValues : undefined,
          }
        } as any);
        await refreshProfile();
        clearDraft();
      }

      if (isRefinement) {
        toast({
          title: "Proposal Refined",
          description: "Updated with your additional details. This refinement does not count against your monthly quota.",
        });
      }
    } catch (err: any) {
      console.error("Error generating proposal:", err);
      toast({ title: "Error", description: err.message || "Failed to generate proposal. Please try again.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!results) return;
    toast({ title: "Exporting PDF", description: "Preparing your proposal..." });

    try {
      const { exportProposalPDF } = await import("@/lib/exportProposalPDF");
      await exportProposalPDF({
        elementId: "security-proposal-content",
        filenamePrefix: "security-proposal",
        industryLabel: "Security Proposal",
        ...clientInfo,
      });
    } catch (error) {
      toast({ title: "Error", description: "Failed to export PDF. Please try again.", variant: "destructive" });
      return;
    }
    toast({ title: "PDF Exported", description: "Your proposal has been downloaded." });
  };

  const hasPropertyInfo = !!(formData?.address && formData?.propertyType);
  const hasCostInfo = staffingItems.length > 0 && staffingTotals.weekly > 0;
  const hasCompanyInfo = !!(clientInfo.companyName || clientInfo.contactName);

  const steps: ProposalStep[] = [
    {
      id: "threat",
      label: "Threat Import",
      icon: <LinkIcon className="h-5 w-5" />,
      completed: !!selectedThreatId,
      content: (
        <ThreatSelector
          selectedThreatId={selectedThreatId}
          onSelect={handleThreatSelect}
          hasUserEdits={hasUserEdits}
        />
      ),
    },
    {
      id: "property",
      label: "Property & Service",
      icon: <MapPin className="h-5 w-5" />,
      completed: hasPropertyInfo,
      content: (
        <ProposalForm
          onChange={(data) => {
            setFormData(data);
            setHasUserEdits(true);
          }}
          hideSubmit
        />
      ),
    },
    {
      id: "staffing",
      label: "Staffing & Cost",
      icon: <DollarSign className="h-5 w-5" />,
      completed: hasCostInfo,
      content: (
        <StaffingTable
          items={staffingItems}
          onChange={setStaffingItems}
          serviceType={formData?.serviceType}
          propertyType={formData?.propertyType}
          showClearance={requiresClearance(formData?.propertyType, formData?.serviceType)}
        />
      ),
    },
    {
      id: "company",
      label: "Your Company Info",
      icon: <Building2 className="h-5 w-5" />,
      completed: hasCompanyInfo,
      content: (
        <ClientCustomization
          info={clientInfo}
          onChange={setClientInfo}
          showLogoUpload={isProfessionalOrAbove}
          showWhiteLabel={isEnterpriseOrStaff}
          industry="security"
        />
      ),
    },
    {
      id: "photos",
      label: "Site Photos",
      icon: <ImagePlus className="h-5 w-5" />,
      completed: photos.length > 0,
      hidden: !isProfessionalOrAbove,
      content: <PhotoUploader photos={photos} onChange={setPhotos} />,
    },
    {
      id: "custom-fields",
      label: "Custom Fields",
      icon: <Sparkles className="h-5 w-5" />,
      completed: hasCustomFields
        ? customFields.every((f) => !f.is_required || (customFieldValues[f.field_key] !== undefined && customFieldValues[f.field_key] !== "" && customFieldValues[f.field_key] !== null))
        : true,
      hidden: !hasCustomFields,
      content: (
        <EnterpriseCustomFieldsStep
          fields={customFields}
          values={customFieldValues}
          onChange={setCustomFieldValues}
        />
      ),
    },
    {
      id: "generate",
      label: "Generate Proposal",
      icon: <FileText className="h-5 w-5" />,
      completed: !!results,
      content: (
        <ProposalBrandingProvider info={clientInfo}>
        <SecurityGenerateStep
          formData={formData}
          staffingItems={staffingItems}
          results={results}
          previousResults={previousResults}
          isLoading={isLoading}
          onGenerate={handleGenerate}
          onExportPDF={handleExportPDF}
          onResultsChange={setResults}
          extraContent={
            hasCustomFields ? (
              <EnterpriseCustomFieldsDisplay fields={customFields} values={customFieldValues} />
            ) : undefined
          }
        />
        </ProposalBrandingProvider>
      ),
    },
  ];

  // Locked feature cards for non-professional users
  const lockedCards = !isProfessionalOrAbove && (
    <div className="space-y-4 mb-6">
      <Card className="border-border bg-card shadow-card cursor-pointer" onClick={() => isDemoUser && unlockFeature("professional")}>
        <CardContent className="flex items-center gap-3 py-4">
          <Lock className="h-5 w-5 text-muted-foreground shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-foreground text-sm">Advanced Cost Calculator</p>
            <p className="text-xs text-muted-foreground">
              {isDemoUser ? "Click to unlock for demo" : "Multiple pricing models available on Professional plan and above."}
            </p>
          </div>
          {!isDemoUser && <Link to="/pricing"><Button variant="outline" size="sm">Upgrade</Button></Link>}
        </CardContent>
      </Card>
      <Card className="border-border bg-card shadow-card cursor-pointer" onClick={() => isDemoUser && unlockFeature("professional")}>
        <CardContent className="flex items-center gap-3 py-4">
          <Lock className="h-5 w-5 text-muted-foreground shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-foreground text-sm">Photo Evidence Analysis</p>
            <p className="text-xs text-muted-foreground">
              {isDemoUser ? "Click to unlock for demo" : "Upload and analyze site photos on Professional plan and above."}
            </p>
          </div>
          {!isDemoUser && <Link to="/pricing"><Button variant="outline" size="sm">Upgrade</Button></Link>}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <AppLayout>
      {!subscription.isLoading && !isSubscribed && (
        <Card className="mb-6 border-warning/50 bg-warning/5 max-w-4xl mx-auto">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <Lock className="h-5 w-5 text-warning" />
              <div>
                <p className="font-semibold text-foreground">Subscription Required</p>
                <p className="text-sm text-muted-foreground">Subscribe to a plan to start generating security proposals.</p>
              </div>
            </div>
            <Link to="/pricing">
              <Button className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">View Plans</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {isSubscribed && isAtLimit && (
        <Alert className="mb-6 border-warning/50 max-w-4xl mx-auto">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>You've used {proposalsUsed}/{proposalLimit} proposals on the {currentTier ? SUBSCRIPTION_TIERS[currentTier].name : ""} plan.</span>
            <Link to="/pricing"><Button variant="outline" size="sm">Upgrade Plan</Button></Link>
          </AlertDescription>
        </Alert>
      )}

      {isSubscribed && !isAtLimit && proposalLimit !== null && proposalLimit > 0 && (
        <p className="text-xs text-muted-foreground mb-4 max-w-4xl mx-auto">Proposals used: {proposalsUsed}/{proposalLimit} this month</p>
      )}

      {lockedCards && <div className="max-w-4xl mx-auto">{lockedCards}</div>}

      {hasDraft && (
        <div className="max-w-4xl mx-auto">
          <DraftRestoreBanner age={draftAge} onRestore={restoreDraft} onDismiss={dismissDraft} />
        </div>
      )}

      <ProposalBuilderFlow steps={steps} />
    </AppLayout>
  );
};

export default ProposalBuilder;
